import { createContext, useContext, useState, useEffect } from 'react'
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut
} from 'firebase/auth'
import { doc, getDoc, setDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { auth, db, functions, googleProvider, appleProvider } from '../lib/firebase'

// Collect browser information for login tracking
async function getBrowserInfo() {
  const info = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages?.join(', '),
    platform: navigator.platform,
    vendor: navigator.vendor,
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    screenSize: `${window.screen.width}x${window.screen.height}`,
    screenColorDepth: window.screen.colorDepth,
    windowSize: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    online: navigator.onLine,
    deviceMemory: navigator.deviceMemory || null,
    hardwareConcurrency: navigator.hardwareConcurrency || null,
    referrer: document.referrer || null,
  }

  // Try to get IP address from a free service
  try {
    const response = await fetch('https://api.ipify.org?format=json', { timeout: 3000 })
    const data = await response.json()
    info.ip = data.ip
  } catch {
    info.ip = null
  }

  return info
}

// Record a login attempt to Firestore
async function recordLoginAttempt({ email, success, reason, provider }) {
  try {
    const browserInfo = await getBrowserInfo()

    await addDoc(collection(db, 'loginAttempts'), {
      email: email?.toLowerCase() || null,
      success,
      reason: reason || null,
      provider: provider || null,
      timestamp: serverTimestamp(),
      ...browserInfo
    })
  } catch (err) {
    // Don't block auth flow if logging fails
    console.error('Failed to record login attempt:', err)
  }
}

const AuthContext = createContext(null)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isOwner, setIsOwner] = useState(false)
  const [isWhitelisted, setIsWhitelisted] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Check whitelist
          const whitelisted = await checkWhitelist(firebaseUser.email)
          setIsWhitelisted(whitelisted)

          if (whitelisted) {
            // Get or create user document
            const userData = await getOrCreateUser(firebaseUser)
            setUser(userData)

            // Check if owner (and set custom claim)
            const ownerStatus = await checkOwnerStatus(firebaseUser)
            setIsOwner(ownerStatus)

            // Record successful login attempt
            recordLoginAttempt({
              email: firebaseUser.email,
              success: true,
              provider: firebaseUser.providerData[0]?.providerId || 'unknown'
            })
          } else {
            // Record denied login attempt - must await before signing out
            await recordLoginAttempt({
              email: firebaseUser.email,
              success: false,
              reason: 'Email not on whitelist',
              provider: firebaseUser.providerData[0]?.providerId || 'unknown'
            })

            // Not whitelisted - sign them out
            await firebaseSignOut(auth)
            setUser(null)
            setIsOwner(false)
            setError('Your email is not on the whitelist. Contact the admin for access.')
          }
        } catch (err) {
          console.error('Auth error:', err)
          setError(err.message)
        }
      } else {
        setUser(null)
        setIsOwner(false)
        setIsWhitelisted(false)
      }
      setLoading(false)
    })

    return unsubscribe
  }, [])

  async function checkWhitelist(email) {
    try {
      const configDoc = await getDoc(doc(db, 'config', 'allowedUsers'))
      if (configDoc.exists()) {
        const { emails = [] } = configDoc.data()
        return emails.includes(email.toLowerCase())
      }
      // If no whitelist exists yet, allow (for initial setup)
      return true
    } catch (err) {
      console.error('Whitelist check failed:', err)
      return false
    }
  }

  async function checkOwnerStatus(firebaseUser) {
    try {
      // Call cloud function to set owner claim
      const setOwnerClaim = httpsCallable(functions, 'setOwnerClaim')
      const result = await setOwnerClaim()

      if (result.data.owner) {
        // Force token refresh to get the new claim
        await firebaseUser.getIdToken(true)
      }

      return result.data.owner
    } catch (err) {
      console.error('Owner check failed:', err)
      // Fallback: check Firestore config (for initial setup before function deployed)
      try {
        const configDoc = await getDoc(doc(db, 'config', 'settings'))
        if (configDoc.exists()) {
          const { ownerEmail } = configDoc.data()
          return ownerEmail?.toLowerCase() === firebaseUser.email?.toLowerCase()
        }
      } catch {
        // Ignore fallback errors
      }
      return false
    }
  }

  async function getOrCreateUser(firebaseUser) {
    const userRef = doc(db, 'users', firebaseUser.uid)
    const userDoc = await getDoc(userRef)

    if (userDoc.exists()) {
      // Update last login
      await setDoc(userRef, { lastLogin: serverTimestamp() }, { merge: true })
      return { uid: firebaseUser.uid, ...userDoc.data() }
    }

    // Create new user
    const userData = {
      email: firebaseUser.email,
      displayName: firebaseUser.displayName || firebaseUser.email.split('@')[0],
      photoURL: firebaseUser.photoURL || null,
      provider: firebaseUser.providerData[0]?.providerId || 'unknown',
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp()
    }

    await setDoc(userRef, userData)
    return { uid: firebaseUser.uid, ...userData }
  }

  async function signInWithGoogle() {
    setError(null)
    try {
      await signInWithPopup(auth, googleProvider)
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError(err.message)
      }
      throw err
    }
  }

  async function signInWithApple() {
    setError(null)
    try {
      await signInWithPopup(auth, appleProvider)
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError(err.message)
      }
      throw err
    }
  }

  async function signOut() {
    try {
      await firebaseSignOut(auth)
    } catch (err) {
      setError(err.message)
      throw err
    }
  }

  function clearError() {
    setError(null)
  }

  const value = {
    user,
    isOwner,
    isWhitelisted,
    loading,
    error,
    signInWithGoogle,
    signInWithApple,
    signOut,
    clearError
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
